// Recommended revision resources for roadmap items.
// Pattern ported from Cyber Agoge portal: exact match first, then keyword matching.

export const RESOURCE_TYPES = [
  'video',
  'course',
  'documentation',
  'practice',
  'book',
  'article',
  'tool',
];

export function getResourceTypeIcon(type) {
  switch (type) {
    case 'video':
      return '🎬';
    case 'course':
      return '📚';
    case 'documentation':
      return '📄';
    case 'practice':
      return '🎯';
    case 'book':
      return '📖';
    case 'article':
      return '📝';
    case 'tool':
      return '🔧';
    default:
      return '📌';
  }
}

export function getResourceTypeColor(type) {
  switch (type) {
    case 'video':
      return 'bg-red-500/20 text-red-300 border-red-500/30';
    case 'course':
      return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 'documentation':
      return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    case 'practice':
      return 'bg-green-500/20 text-green-300 border-green-500/30';
    case 'book':
      return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 'article':
      return 'bg-yellow-500/20 text-yellow-200 border-yellow-500/30';
    case 'tool':
      return 'bg-orange-500/20 text-orange-200 border-orange-500/30';
    default:
      return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
  }
}

// Keep this catalog small and high-signal; we can expand iteratively.
export const roadmapResources = [
  {
    itemTitle: 'Git, GitHub and Visual Studio Code',
    keywords: ['git', 'github', 'visual studio code', 'vscode'],
    resources: [
      {
        title: 'Git & GitHub Crash Course',
        type: 'video',
        url: 'https://www.youtube.com/watch?v=RGOj5yH7evk',
        description: 'Fast overview of Git basics + GitHub workflow.',
        free: true,
      },
      {
        title: 'Learn Git Branching',
        type: 'practice',
        url: 'https://learngitbranching.js.org/',
        description: 'Interactive Git tutorial to build confidence quickly.',
        free: true,
      },
      {
        title: 'VS Code Docs',
        type: 'documentation',
        url: 'https://code.visualstudio.com/docs',
        description: 'Official VS Code docs and beginner guides.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'Command Line and Terminal',
    keywords: ['command line', 'terminal', 'cli', 'bash', 'shell', 'linux'],
    resources: [
      {
        title: 'Linux Command Line Basics (Udacity)',
        type: 'course',
        url: 'https://www.udacity.com/course/linux-command-line-basics--ud595',
        description: 'Short beginner course on CLI fundamentals.',
        free: true,
      },
      {
        title: 'OverTheWire Bandit',
        type: 'practice',
        url: 'https://overthewire.org/wargames/bandit/',
        description: 'Gamified terminal practice (security-flavoured).',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'Python Fundamentals',
    keywords: ['python', 'programming', 'python fundamentals'],
    resources: [
      {
        title: 'Python for Everybody',
        type: 'course',
        url: 'https://www.py4e.com/',
        description: 'Beginner-friendly Python course.',
        free: true,
      },
      {
        title: 'Automate the Boring Stuff',
        type: 'book',
        url: 'https://automatetheboringstuff.com/',
        description: 'Practical Python projects and automation.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'Security Fundamentals',
    keywords: ['security fundamentals', 'security basics', 'cybersecurity'],
    resources: [
      {
        title: 'TryHackMe Pre-Security',
        type: 'course',
        url: 'https://tryhackme.com/path/outline/presecurity',
        description: 'Beginner security pathway.',
        free: true,
      },
      {
        title: 'OWASP Top 10',
        type: 'documentation',
        url: 'https://owasp.org/www-project-top-ten/',
        description: 'Key web vulnerabilities everyone should know.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'DevSecOps Fundamentals',
    keywords: ['devsecops', 'threat modelling', 'sast', 'dast', 'sca', 'iac scanning'],
    resources: [
      {
        title: 'OWASP DevSecOps Guideline',
        type: 'documentation',
        url: 'https://owasp.org/www-project-devsecops-guideline/',
        description: 'Practical DevSecOps implementation guide.',
        free: true,
      },
      {
        title: 'Threat Modeling Manifesto',
        type: 'documentation',
        url: 'https://www.threatmodelingmanifesto.org/',
        description: 'Core threat modeling principles.',
        free: true,
      },
      {
        title: 'SAST vs DAST (Snyk)',
        type: 'article',
        url: 'https://snyk.io/learn/sast-vs-dast/',
        description: 'Understand the difference and when to use each.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'AI/ML Fundamentals',
    keywords: ['ai', 'ml', 'machine learning', 'ai/ml'],
    resources: [
      {
        title: 'AI for Everyone (Andrew Ng)',
        type: 'course',
        url: 'https://www.coursera.org/learn/ai-for-everyone',
        description: 'Non-technical primer on core AI concepts.',
        free: true,
      },
      {
        title: 'Machine Learning (3Blue1Brown)',
        type: 'video',
        url: 'https://www.youtube.com/playlist?list=PLZHQObOWTQDMsr9K-rj53DwVRMYO3t5Yr',
        description: 'Intuition-first explanations of ML ideas.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'Interview Prep and CV',
    keywords: ['interview', 'cv', 'resume', 'job', 'career'],
    resources: [
      {
        title: 'Tech Interview Handbook',
        type: 'documentation',
        url: 'https://www.techinterviewhandbook.org/',
        description: 'Structured interview prep guidance.',
        free: true,
      },
      {
        title: 'Pramp Mock Interviews',
        type: 'practice',
        url: 'https://www.pramp.com/',
        description: 'Practice interviews with peers.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'AWS Cloud Practitioner',
    keywords: ['aws cloud practitioner', 'cloud practitioner', 'clf', 'aws practitioner'],
    resources: [
      {
        title: 'AWS Cloud Practitioner Essentials',
        type: 'course',
        url: 'https://explore.skillbuilder.aws/learn/course/external/view/elearning/134/aws-cloud-practitioner-essentials',
        description: 'Official free AWS training.',
        free: true,
      },
      {
        title: 'freeCodeCamp AWS Course',
        type: 'video',
        url: 'https://www.youtube.com/watch?v=SOTamWNgDKc',
        description: 'Long-form video course covering the exam.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'Terraform Associate',
    keywords: ['terraform', 'hashicorp', 'iac', 'infrastructure as code', 'terraform associate'],
    resources: [
      {
        title: 'Terraform Learn (HashiCorp)',
        type: 'documentation',
        url: 'https://developer.hashicorp.com/terraform/tutorials',
        description: 'Official Terraform tutorials and hands-on walkthroughs.',
        free: true,
      },
      {
        title: 'Terraform Docs',
        type: 'documentation',
        url: 'https://developer.hashicorp.com/terraform/docs',
        description: 'Official Terraform documentation.',
        free: true,
      },
      {
        title: 'Terraform Certification Prep',
        type: 'course',
        url: 'https://developer.hashicorp.com/terraform/tutorials/certification-003',
        description: 'Official prep resources for the Terraform Associate exam.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'Apache Airflow Fundamentals',
    keywords: ['airflow', 'apache airflow', 'dag', 'data pipeline orchestration', 'orchestration'],
    resources: [
      {
        title: 'Airflow Documentation',
        type: 'documentation',
        url: 'https://airflow.apache.org/docs/',
        description: 'Official Apache Airflow docs (start here).',
        free: true,
      },
      {
        title: 'Airflow Tutorial',
        type: 'documentation',
        url: 'https://airflow.apache.org/docs/apache-airflow/stable/tutorial/index.html',
        description: 'Official tutorial: DAGs, operators, scheduling, and UI basics.',
        free: true,
      },
      {
        title: 'Astronomer Airflow Guides',
        type: 'article',
        url: 'https://www.astronomer.io/guides/',
        description: 'High-quality practical guides for production Airflow.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'Data Engineering Basics',
    keywords: ['data engineering', 'etl', 'elt', 'pipelines', 'data pipeline'],
    resources: [
      {
        title: 'Data Engineering on Google Cloud (free materials)',
        type: 'course',
        url: 'https://www.cloudskillsboost.google/paths/16',
        description: 'Practical data engineering concepts; some labs may require credits.',
        free: false,
      },
      {
        title: 'The Data Engineering Cookbook',
        type: 'documentation',
        url: 'https://github.com/andkret/Cookbook',
        description: 'A broad, structured set of data engineering notes/resources.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'AWS AI Practitioner',
    keywords: ['aws ai practitioner', 'ai practitioner', 'aws ai', 'aws ml'],
    resources: [
      {
        title: 'AWS ML Learning Plan (Skill Builder)',
        type: 'course',
        url: 'https://explore.skillbuilder.aws/learn/lp/28/machine-learning-learning-plan',
        description: 'Official AWS machine learning learning plan.',
        free: true,
      },
      {
        title: 'AWS AI Services',
        type: 'documentation',
        url: 'https://aws.amazon.com/ai/',
        description: 'Overview of AWS AI services and how they fit together.',
        free: true,
      },
    ],
  },
  {
    itemTitle: 'CompTIA Security+',
    keywords: ['comptia', 'security+', 'sec+'],
    resources: [
      {
        title: 'Professor Messer Security+ (SY0-701)',
        type: 'video',
        url: 'https://www.professormesser.com/security-plus/sy0-701/sy0-701-video/sy0-701-comptia-security-plus-course/',
        description: 'Free full Security+ video course.',
        free: true,
      },
      {
        title: 'CompTIA Security+ Exam Page',
        type: 'documentation',
        url: 'https://www.comptia.org/certifications/security',
        description: 'Official exam objectives, pricing, and details.',
        free: true,
      },
    ],
  },
];

export function getResourcesForItem(itemTitle) {
  const normalizedTitle = String(itemTitle || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s+/-]/g, ' ')
    .replace(/\s+/g, ' ');
  if (!normalizedTitle) return [];

  const exactMatch = roadmapResources.find(
    (r) =>
      String(r.itemTitle || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s+/-]/g, ' ')
        .replace(/\s+/g, ' ') === normalizedTitle
  );
  if (exactMatch) return exactMatch.resources || [];

  const keywordMatch = roadmapResources.find((r) =>
    (r.keywords || []).some((kw) => normalizedTitle.includes(String(kw).toLowerCase()))
  );
  if (keywordMatch) return keywordMatch.resources || [];

  return [];
}

